export const TIME_RANGE_SEPARATOR = ' → ';

export type TimeRangeItem = {
    id: number;
    timeRange: string;
};

export type SubtitleLike = {
    id: number;
    origin_start_time: string;
    origin_end_time: string;
    post_start_time?: string;
    post_end_time?: string;
    origin_text?: string;
    post_text?: string;
};

export type ProcessedSubtitle<T extends SubtitleLike> = {
    subtitle: T;
    startTime: number;
    endTime: number;
    id: number;
};

export const parseTimeToSeconds = (timeString: string): number => {
    if (!timeString) return 0;

    try {
        const normalized = timeString.trim().replace(',', '.');
        const parts = normalized.split(':');

        if (parts.length === 3) {
            const hours = parseInt(parts[0], 10) || 0;
            const minutes = parseInt(parts[1], 10) || 0;
            const seconds = parseFloat(parts[2]) || 0;
            return hours * 3600 + minutes * 60 + seconds;
        }

        if (parts.length === 2) {
            const minutes = parseInt(parts[0], 10) || 0;
            const seconds = parseFloat(parts[1]) || 0;
            return minutes * 60 + seconds;
        }

        if (parts.length === 1) {
            return parseFloat(parts[0]) || 0;
        }

        return 0;
    } catch {
        return 0;
    }
};

export const formatTimelineTime = (seconds: number): string => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(seconds, 0) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = (safeSeconds % 60).toFixed(3);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.padStart(6, '0')}`;
};

export const formatSecondsToTimestamp = (
    seconds: number,
    millisecondSeparator: ',' | '.' = ','
): string => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(seconds, 0) : 0;
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const wholeSeconds = Math.floor(safeSeconds % 60);
    const ms = Math.round((safeSeconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${wholeSeconds
        .toString()
        .padStart(2, '0')}${millisecondSeparator}${ms.toString().padStart(3, '0')}`;
};

export const formatTimeStringForVtt = (timeString: string): string => {
    if (!timeString) return '00:00:00.000';

    const normalized = timeString.replace(',', '.');
    const parts = normalized.split(':');
    let hours = '00';
    let minutes = '00';
    let secondsMs = '00.000';

    if (parts.length === 3) {
        [hours, minutes] = parts;
        secondsMs = parts[2].includes('.') ? parts[2] : `${parts[2]}.000`;
    } else if (parts.length === 2) {
        [minutes] = parts;
        secondsMs = parts[1].includes('.') ? parts[1] : `${parts[1]}.000`;
    } else if (parts.length === 1) {
        secondsMs = parts[0].includes('.') ? parts[0] : `${parts[0]}.000`;
    }

    const pad2 = (value: string) => value.padStart(2, '0');
    const ensureMs = (value: string) => {
        const [secs, ms = '000'] = value.split('.');
        return `${pad2(secs)}.${ms.padEnd(3, '0').slice(0, 3)}`;
    };

    return `${pad2(hours)}:${pad2(minutes)}:${ensureMs(secondsMs)}`;
};

export const buildVttFromSubtitles = <T extends SubtitleLike>(subtitles: T[]): string => {
    const lines: string[] = ['WEBVTT', ''];

    subtitles.forEach((subtitle, index) => {
        const start = formatTimeStringForVtt(subtitle.post_start_time || subtitle.origin_start_time);
        const end = formatTimeStringForVtt(subtitle.post_end_time || subtitle.origin_end_time);
        const text = (subtitle.post_text || subtitle.origin_text || '').replace(/\r?\n/g, '\n');

        lines.push(String(index + 1));
        lines.push(`${start} --> ${end}`);
        lines.push(text);
        lines.push('');
    });

    return lines.join('\n');
};

export const splitTimeRange = (timeRange?: string): [string, string] | null => {
    if (!timeRange) return null;

    const parts = timeRange.split(TIME_RANGE_SEPARATOR);
    if (parts.length !== 2) return null;

    return [parts[0].trim(), parts[1].trim()];
};

export const buildTimeRange = (startTime: string, endTime: string): string =>
    `${startTime}${TIME_RANGE_SEPARATOR}${endTime}`;

export const parseTimeRangeToSeconds = (timeRange?: string): { startTime: number; endTime: number } | null => {
    const parts = splitTimeRange(timeRange);
    if (!parts) return null;

    return {
        startTime: parseTimeToSeconds(parts[0]),
        endTime: parseTimeToSeconds(parts[1]),
    };
};

export const buildTimeRangeMap = <T extends TimeRangeItem>(items?: T[]): Map<number, { timeRange: string }> => {
    const timeRangeMap = new Map<number, { timeRange: string }>();

    items?.forEach((item) => {
        if (item.timeRange) {
            timeRangeMap.set(item.id, { timeRange: item.timeRange });
        }
    });

    return timeRangeMap;
};

export const resolveSubtitleTimeWindow = <T extends SubtitleLike>(
    subtitle: T,
    targetData?: { timeRange: string }
): { startTime: number; endTime: number } => {
    const parsedRange = parseTimeRangeToSeconds(targetData?.timeRange);
    if (parsedRange) {
        return parsedRange;
    }

    return {
        startTime: parseTimeToSeconds(subtitle.post_start_time || subtitle.origin_start_time),
        endTime: parseTimeToSeconds(subtitle.post_end_time || subtitle.origin_end_time),
    };
};

export const preprocessSubtitlesByTargetTime = <T extends SubtitleLike>(
    subtitles?: T[],
    targetItems?: TimeRangeItem[]
): {
    processed: ProcessedSubtitle<T>[];
    targetMap: Map<number, { timeRange: string }>;
} => {
    if (!subtitles?.length) {
        return {
            processed: [],
            targetMap: new Map<number, { timeRange: string }>(),
        };
    }

    const targetMap = buildTimeRangeMap(targetItems);
    const processed: ProcessedSubtitle<T>[] = [];

    for (const subtitle of subtitles) {
        if (!subtitle.post_start_time || !subtitle.post_end_time) {
            continue;
        }

        const { startTime, endTime } = resolveSubtitleTimeWindow(subtitle, targetMap.get(subtitle.id));

        processed.push({
            subtitle,
            startTime,
            endTime,
            id: subtitle.id,
        });
    }

    processed.sort((left, right) => left.startTime - right.startTime);

    return {
        processed,
        targetMap,
    };
};
